import type { Workout, MonthlySummary } from '../../domain/workouts/types';
import { formatLoadAndReps } from '../../domain/workouts/describeLift';
import { groupByDay, formatDayHeading } from '../../domain/workouts/dayGroups';
import '../../components/DayGroup/DayGroup.css';

export interface HistoryViewProps {
  workouts: Workout[];
  summary: MonthlySummary | null;
  previousSummary: MonthlySummary | null;
  isLoading: boolean;
  isCurrentMonth: boolean;
  /** When provided (and viewing the current month), renders a delete button per workout. */
  onDeleteWorkout?: (workout: Workout) => void;
  /** Message shown in the empty state when no workouts exist. */
  emptyHint?: string;
}

/**
 * Presentational view of a single month's history: the monthly-bests summary
 * card plus the workout list. Shared by the history page and the leaderboard
 * user-history modal.
 */
export function HistoryView({
  workouts,
  summary,
  previousSummary,
  isLoading,
  isCurrentMonth,
  onDeleteWorkout,
  emptyHint,
}: HistoryViewProps) {
  if (isLoading) {
    return <div className="history-page__loading">Loading...</div>;
  }

  const renderSummaryCard = () => {
    if (!summary) return null;

    const totalRounded = Math.round(summary.totalPoints);
    const prevTotalRounded = previousSummary ? Math.round(previousSummary.totalPoints) : 0;
    const delta = previousSummary ? totalRounded - prevTotalRounded : 0;

    // This row is one narrow line, so the name gets whatever the load leaves it:
    // "Bodyweight × 7" needs more room than "165 lb × 2", and a fixed truncation
    // let the longer ones overflow the column.
    const SUMMARY_BUDGET = 28;
    const formatBest = (best: typeof summary.pushBest) => {
      if (!best?.exerciseName) return '-';
      // Shared formatter, so a bodyweight pull-up reads "Bodyweight × 7" rather
      // than the nonsensical "0×7".
      const load = formatLoadAndReps(best);
      const room = Math.max(8, SUMMARY_BUDGET - load.length - 3);
      const name =
        best.exerciseName.length > room
          ? best.exerciseName.substring(0, room - 1).trim() + '…'
          : best.exerciseName;
      return `${name} - ${load}`;
    };

    return (
      <div className="history-summary">
        <h3 className="history-summary__title">Monthly Bests</h3>
        <div className="history-summary__grid">
          {(
            [
              ['Push', summary.pushBest],
              ['Pull', summary.rowBest],
              ['Hinge', summary.hingeBest],
              ['Squat', summary.squatBest],
            ] as const
          ).map(([label, best]) => (
            <div className="history-summary__row" key={label}>
              <span className="history-summary__label">{label}</span>
              <span className="history-summary__value">{formatBest(best)}</span>
              <span className="history-summary__points">{Math.round(best?.points || 0)} pts</span>
            </div>
          ))}
          <div className="history-summary__row history-summary__row--total">
            <span className="history-summary__label">Total</span>
            <span className="history-summary__value">
              {delta !== 0 && (
                <span
                  className={`history-summary__delta ${delta > 0 ? 'history-summary__delta--positive' : 'history-summary__delta--negative'}`}
                >
                  {delta > 0 ? '↑' : '↓'} {Math.abs(delta)} pts
                </span>
              )}
            </span>
            <span className="history-summary__points">{totalRounded} pts</span>
          </div>
        </div>
      </div>
    );
  };

  // Newest first. Within a single day the most recently logged comes first, so
  // the order is stable rather than however the query happened to return them.
  const sortedWorkouts = [...workouts].sort((a, b) => {
    const byDate = b.workoutDate.localeCompare(a.workoutDate);
    if (byDate !== 0) return byDate;
    return (b.createdAt?.getTime?.() ?? 0) - (a.createdAt?.getTime?.() ?? 0);
  });

  const canDelete = isCurrentMonth && !!onDeleteWorkout;

  return (
    <>
      {renderSummaryCard()}

      <div className="history-list">
        <h3 className="history-list__title">Workouts</h3>
        {sortedWorkouts.length === 0 ? (
          <div className="history-list__empty">
            <p>No lifts logged this month.</p>
            {isCurrentMonth && emptyHint && <p>{emptyHint}</p>}
          </div>
        ) : (
          <div className="day-groups">
            {groupByDay(sortedWorkouts).map((day) => (
              <section className="day-group" key={day.date}>
                <h4 className="day-group__label">{formatDayHeading(day.date)}</h4>
                <ul className="day-group__items">
                  {day.items.map((workout) => {
                    const isBest =
                      summary?.pushBest?.workoutId === workout.id ||
                      summary?.rowBest?.workoutId === workout.id ||
                      summary?.hingeBest?.workoutId === workout.id ||
                      summary?.squatBest?.workoutId === workout.id;
                    return (
                      <li
                        key={workout.id}
                        className={`history-item ${isBest ? 'history-item--best' : ''}`}
                      >
                        <div className="history-item__details">
                          <div className="history-item__exercise">
                            {workout.exerciseNameSnapshot}
                          </div>
                          <div className="history-item__stats">{formatLoadAndReps(workout)}</div>
                        </div>
                        <div className="history-item__score">
                          <div className="history-item__points">
                            {Math.round(workout.points)} pts
                          </div>
                          {isBest && <span className="history-item__badge">BEST</span>}
                        </div>
                        {canDelete && (
                          <button
                            className="history-item__delete"
                            onClick={() => onDeleteWorkout!(workout)}
                            aria-label="Delete workout"
                          >
                            <span aria-hidden="true">×</span>
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
